import { THREE } from 'uweb/three'
import { maptalks, MapView } from 'uweb/maptalks'
import { Point, UTM, colorize } from 'uweb/utils'
import { KeyValue, Safe, Loop, Delay, log } from 'utils/web'

import { computeBoundsTree, disposeBoundsTree, acceleratedRaycast } from 'three-mesh-bvh'

THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree
THREE.Mesh.prototype.raycast = acceleratedRaycast

const bakeForRaycast = (im: THREE.InstancedMesh): THREE.Mesh => {
    im.updateMatrixWorld(true)
    const base = im.geometry.getAttribute('position')
    const out: number[] = []
    const m = new THREE.Matrix4()
    const v = new THREE.Vector3()
    for (let i = 0; i < im.count; i++) {
        im.getMatrixAt(i, m)
        for (let j = 0; j < base.count; j++) {
            v.fromBufferAttribute(base, j).applyMatrix4(m).applyMatrix4(im.matrixWorld)
            out.push(v.x, v.y, v.z)   // world space
        }
    }
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.Float32BufferAttribute(out, 3))
    g.computeBoundsTree()              // build the BVH once
    // const mesh = new THREE.Mesh(g)
    const mesh = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ side: THREE.DoubleSide }))
    mesh.matrixAutoUpdate = false      // verts already world-space, identity matrix
    return mesh
}

class DynamicSurface {
    threeLayer: any
    opts: { maxVertices?: number, maxIndices?: number, zone?: number, hemisphere?: 'N' | 'S' }
    mesh: THREE.Mesh
    line: THREE.Mesh
    geometry = new THREE.BufferGeometry()
    posArr: Float32Array
    colArr: Float32Array                            // ← soil colors
    idxArr: Uint32Array
    vertexCount = 0
    indexCount = 0
    vertexMap = new Map<string, number>()
    triangleSet = new Set<string>()
    anchor: THREE.Vector3 | null = null

    // soil palette + running z-range for the gradient
    topsoil = new THREE.Color(0x4a3220)
    subsoil = new THREE.Color(0x6f4e37)
    _c = new THREE.Color()
    zMin = Infinity
    zMax = -Infinity

    rayTarget: THREE.Mesh | null = null
    targetTopZ = 0

    constructor(threeLayer: any, opts: DynamicSurface['opts'] = {}) {
        this.threeLayer = threeLayer
        this.opts = opts
        const maxV = opts.maxVertices ?? 200000
        const maxI = opts.maxIndices ?? 600000

        this.posArr = new Float32Array(maxV * 3)
        this.colArr = new Float32Array(maxV * 3)
        this.idxArr = new Uint32Array(maxI)

        const posAttr = new THREE.BufferAttribute(this.posArr, 3)
        posAttr.setUsage(THREE.DynamicDrawUsage)
        this.geometry.setAttribute('position', posAttr)

        const colAttr = new THREE.BufferAttribute(this.colArr, 3)
        colAttr.setUsage(THREE.DynamicDrawUsage)
        this.geometry.setAttribute('color', colAttr)

        const idxAttr = new THREE.BufferAttribute(this.idxArr, 1)
        idxAttr.setUsage(THREE.DynamicDrawUsage)
        this.geometry.setIndex(idxAttr)
        this.geometry.setDrawRange(0, 0)

        // topsoil — matte, opaque, vertex-colored. polygonOffset so the wireframe sits on top
        const material = new THREE.MeshStandardMaterial({
            color: 0xffffff,            // white → vertex colors show true
            vertexColors: true,
            flatShading: true,
            roughness: 0.97,
            metalness: 0,
            side: THREE.DoubleSide,
            polygonOffset: true,
            polygonOffsetFactor: 1,
            polygonOffsetUnits: 1,
        })
        this.mesh = new THREE.Mesh(this.geometry, material)
        this.mesh.frustumCulled = false

        // wireframe shares the SAME geometry → respects drawRange, updates live
        const wireMaterial = new THREE.MeshBasicMaterial({
            color: 0x2a1c10,
            wireframe: true,
            transparent: true,
            opacity: 0.25,
        })
        this.line = new THREE.Mesh(this.geometry, wireMaterial)
        this.line.frustumCulled = false
        this.line.renderOrder = 1
    }

    // stable per-vertex grain so recolor() doesn't flicker
    hash = (n: number) => {
        const s = Math.sin(n * 127.1) * 43758.5453
        return s - Math.floor(s)
    }

    writeSoilColor = (idx: number, localZ: number) => {
        const span = (this.zMax - this.zMin) || 1
        const t = Math.max(0, Math.min(1, (localZ - this.zMin) / span))   // 0 bottom → 1 top
        this._c.lerpColors(this.subsoil, this.topsoil, t)
        const j = 0.85 + this.hash(idx) * 0.3
        this.colArr[idx * 3] = this._c.r * j
        this.colArr[idx * 3 + 1] = this._c.g * j
        this.colArr[idx * 3 + 2] = this._c.b * j
    }

    project = (east: number, north: number, elev: number): [number, number, number] => {
        const is_3D_enabled = KeyValue('3D') === 'yes'
        const is_3D_diff = Number(KeyValue('Elevation'))
        const { lat, lng } = UTM.convertUtmToLatLng(east, north, this.opts.zone ?? 48, this.opts.hemisphere ?? 'N')
        const xy = this.threeLayer.coordinateToVector3(new maptalks.Coordinate(lng, lat), 0)
        const z = is_3D_enabled ? this.threeLayer.distanceToVector3(elev - is_3D_diff, elev - is_3D_diff).x : 0
        if (!this.anchor) {
            this.anchor = new THREE.Vector3(xy.x, xy.y, z)
            this.mesh.position.copy(this.anchor)
            this.line.position.copy(this.anchor)   // ← keep wireframe aligned
        }
        return [xy.x - this.anchor.x, xy.y - this.anchor.y, z - this.anchor.z]
    }

    addTriangles = (positions: number[]) => {
        const posAttr = this.geometry.getAttribute('position') as THREE.BufferAttribute
        const colAttr = this.geometry.getAttribute('color') as THREE.BufferAttribute
        const idxAttr = this.geometry.getIndex() as THREE.BufferAttribute
        const vStart = this.vertexCount
        const iStart = this.indexCount

        const resolve = (o: number): number => {
            const key = `${positions[o]},${positions[o + 1]},${positions[o + 2]}`
            let idx = this.vertexMap.get(key)
            if (idx === undefined) {
                const [x, y, z] = this.project(positions[o], positions[o + 1], positions[o + 2])
                idx = this.vertexCount
                this.posArr[idx * 3] = x
                this.posArr[idx * 3 + 1] = y
                this.posArr[idx * 3 + 2] = z
                if (z < this.zMin) this.zMin = z
                if (z > this.zMax) this.zMax = z
                this.writeSoilColor(idx, z)
                this.vertexMap.set(key, idx)
                this.vertexCount++
            }
            return idx
        }

        for (let i = 0; i + 8 < positions.length; i += 9) {
            const i0 = resolve(i)
            const i1 = resolve(i + 3)
            const i2 = resolve(i + 6)

            if (i0 === i1 || i1 === i2 || i0 === i2) continue

            const triKey = [i0, i1, i2].sort((a, b) => a - b).join(',')
            if (this.triangleSet.has(triKey)) continue
            this.triangleSet.add(triKey)

            this.idxArr[this.indexCount++] = i0
            this.idxArr[this.indexCount++] = i1
            this.idxArr[this.indexCount++] = i2
        }

        if (this.vertexCount > vStart) {
            const vOff = vStart * 3
            const vCnt = (this.vertexCount - vStart) * 3
            posAttr.updateRange.offset = vOff
            posAttr.updateRange.count = vCnt
            posAttr.needsUpdate = true
            colAttr.updateRange.offset = vOff
            colAttr.updateRange.count = vCnt
            colAttr.needsUpdate = true
        }
        if (this.indexCount > iStart) {
            idxAttr.updateRange.offset = iStart
            idxAttr.updateRange.count = this.indexCount - iStart
            idxAttr.needsUpdate = true
            this.geometry.setDrawRange(0, this.indexCount)
        }
        this.threeLayer.redraw?.()
    }

    // recompute every used vertex's color against the final z-range (call after bulk adds)
    recolor = () => {
        for (let i = 0; i < this.vertexCount; i++) this.writeSoilColor(i, this.posArr[i * 3 + 2])
        const colAttr = this.geometry.getAttribute('color') as THREE.BufferAttribute
        colAttr.updateRange.offset = 0
        colAttr.updateRange.count = this.vertexCount * 3
        colAttr.needsUpdate = true
        this.threeLayer.redraw?.()
    }

    updateVertex = (vertexIndex: number, east: number, north: number, elev: number) => {
        const [x, y, z] = this.project(east, north, elev)
        const posAttr = this.geometry.getAttribute('position') as THREE.BufferAttribute
        const colAttr = this.geometry.getAttribute('color') as THREE.BufferAttribute
        this.posArr[vertexIndex * 3] = x
        this.posArr[vertexIndex * 3 + 1] = y
        this.posArr[vertexIndex * 3 + 2] = z
        this.writeSoilColor(vertexIndex, z)
        posAttr.updateRange.offset = vertexIndex * 3
        posAttr.updateRange.count = 3
        posAttr.needsUpdate = true
        colAttr.updateRange.offset = vertexIndex * 3
        colAttr.updateRange.count = 3
        colAttr.needsUpdate = true
        this.threeLayer.redraw?.()
    }

    setTarget = (target: THREE.Mesh | THREE.InstancedMesh) => {
        this.disposeTarget()
        this.rayTarget = bakeForRaycast(target)
        this.rayTarget.geometry.computeBoundingBox()
        this.targetTopZ = this.rayTarget.geometry.boundingBox!.max.z + 1
    }

    disposeTarget = () => {
        if (!this.rayTarget) return
        this.rayTarget.geometry.disposeBoundsTree?.()
        this.rayTarget.geometry.dispose()
        this.rayTarget = null
    }

    measureVolume = (target?: THREE.Mesh | THREE.InstancedMesh) => {
        if (target) this.setTarget(target)
        if (!this.rayTarget) return null

        const unitsPerMeter = this.threeLayer.distanceToVector3(1, 1).x
        const u3 = unitsPerMeter ** 3
        const rayTarget = this.rayTarget
        const topZ = this.targetTopZ

        const raycaster = new THREE.Raycaster()
        const down = new THREE.Vector3(0, 0, -1)
        const origin = new THREE.Vector3()
        const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3()
        const ax = this.anchor ?? new THREE.Vector3()

        const worldAt = (vi: number, out: THREE.Vector3) =>
            out.set(this.posArr[vi * 3] + ax.x, this.posArr[vi * 3 + 1] + ax.y, this.posArr[vi * 3 + 2] + ax.z)

        const gapAt = (p: THREE.Vector3): number | null => {
            origin.set(p.x, p.y, topZ)
            raycaster.set(origin, down)
            const hits = raycaster.intersectObject(rayTarget, false)
            return hits.length ? p.z - hits[0].point.z : null
        }

        const planArea = (p: THREE.Vector3, q: THREE.Vector3, r: THREE.Vector3) =>
            Math.abs((q.x - p.x) * (r.y - p.y) - (r.x - p.x) * (q.y - p.y)) * 0.5

        let cut = 0, fill = 0, used = 0, skipped = 0

        for (let k = 0; k < this.indexCount; k += 3) {
            worldAt(this.idxArr[k], a)
            worldAt(this.idxArr[k + 1], b)
            worldAt(this.idxArr[k + 2], c)

            const ga = gapAt(a), gb = gapAt(b), gc = gapAt(c)
            if (ga === null || gb === null || gc === null) { skipped++; continue }

            const vol = planArea(a, b, c) * ((ga + gb + gc) / 3)
            vol >= 0 ? cut += vol : fill += -vol
            used++
        }

        return {
            cut_m3: cut / u3,
            fill_m3: fill / u3,
            net_m3: (cut - fill) / u3,
            triangles: used,
            skipped,
        }
    }
}

export class ViewAsMesh {

    cfg: iArgs
    obj: any = {}

    constructor(cfg: iArgs) {

        this.cfg = cfg

    }

    addWithFilter = (key: string, ref = 'UTM', res: any[]) => {

        console.log(key)
        this.removeStartsWith(key)

        const [zone, hemisphere]: [number, 'N' | 'S'] = KeyValue('Zone').split(',').map((v, i) => i === 0 ? Number(v) : v) as [number, 'N' | 'S']
        const threeLayer = this.cfg.MapView?.threeLayer
        const surface = new DynamicSurface(threeLayer, { zone, hemisphere })

        threeLayer?.getScene().add(surface.mesh)
        threeLayer?.getScene().add(surface.line)

        this.obj[`${key}-surface`] = surface.mesh
        this.obj[`${key}-line`] = surface.line
        this.cfg.MapView?.threeLayer.addMesh(surface.mesh)
        this.cfg.MapView?.threeLayer.addMesh(surface.line)

        for (const x of res) {

            const { qr, rows } = x
            const { positions, triangleCount } = rows
            surface.addTriangles(positions)
            surface.recolor()

        }

        setTimeout(() => {

            console.log('Starting raycast between meshes...')
            const mapView = this.cfg.MapView

            mapView?.threeLayer.getScene().traverse((obj: any) => {

                if (obj.userData && obj.userData.type === 'target-surface' && mapView) {

                    const target = obj.children[0]
                    surface.setTarget(target)
                    const stats = surface.measureVolume()           // ← reuses cached bake
                    console.log(stats)

                }

            })

        }, 5000)

    }

    removeStartsWith = (key: string) => {

        if (this.obj) for (const x in this.obj) if (x.indexOf(key) === 0) {

            this.cfg.MapView?.threeLayer.removeMesh(this.obj[x])
            delete this.obj[x]

        }

    }

}